import TagManager from '@sooro-io/react-gtm-module';
import i18n from '../i18n/i18n';

/**
 * Extract English text from i18n key for dataLayer tracking
 * Ensures all analytics data is in English regardless of UI language
 * 
 * @param {string} i18nKey - The i18n key (e.g., 'carousel.titles.shopping')
 * @returns {string} - English text (e.g., 'Shopping')
 */
export const getEnglishText = (i18nKey) => {
  if (!i18nKey) return '';
  
  try {
    // Force English locale for tracking
    const englishText = i18n.t(i18nKey, { lng: 'en' });
    
    // Remove HTML tags (like <sup>) if present
    const cleanText = englishText.replace(/<[^>]*>/g, '');
    
    return cleanText;
  } catch (error) {
    console.error('Error extracting English text:', error);
    return i18nKey;
  }
};

export const trackPageView = (hreflang) => {
  // Clear previous page and user data
  TagManager.dataLayer({
    dataLayer: {
      page_data: null,
      user_data: null
    }
  });
  
  // Get language from hreflang (en-us: en, zh-hk: zh)
  const language = hreflang ? hreflang.split('-')[0] : 'en';
  
  // Determine country from hreflang
  const getCountry = (hreflang) => {
    if (hreflang?.includes('hk')) return 'HK';
    if (hreflang?.includes('us')) return 'US';
    return 'US'; // default
  };
  
  // Determine region from hreflang
  const getRegion = (hreflang) => {
    if (hreflang?.includes('hk')) return 'APAC';
    if (hreflang?.includes('us')) return 'NA';
    return 'NA'; // default
  };
  
  // Determine environment from hostname
  const getEnvironment = () => {
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'dev';
    } else if (hostname === 'mfsimulator-stag.jnjvision.com') {
      return 'staging';
    } else if (hostname === 'mfsimulator.jnjvision.com') {
      return 'prod';
    }
    // Fallback for any other domains
    return 'dev';
  };
  
  // Push page view event
  TagManager.dataLayer({
    dataLayer: {
      event: 'page_view',
      page_data: {
        language: language,
        page_hostname: `${window.location.protocol}//${window.location.hostname}/${hreflang || 'en-us'}`,
        page_location: window.location.href,
        page_referrer: document.referrer || '',
        page_title: document.title,
        site_brand: 'pro',
        site_country: getCountry(hreflang),
        site_environment: getEnvironment(),
        site_region: getRegion(hreflang),
      },
      user_data: {
        user_id: '', // No authentication in this app
        user_login_state: 'anonymous',
        user_login_type: '',
        user_role: ''
      }
    }
  });
};

// Track click events according to GTM specifications
export const trackClick = ({ componentName, type, linkText, linkUrl, linkHostname, outbound }) => {
  // Clear previous event data
  TagManager.dataLayer({
    dataLayer: {
      event_data: null
    }
  });
  
  // Determine if link is outbound (different domain)
  const isOutbound = () => {
    if (!linkUrl) return false;
    try {
      const linkDomain = new URL(linkUrl).hostname;
      const currentDomain = window.location.hostname;
      return linkDomain !== currentDomain;
    } catch {
      return false;
    }
  };
  
  // Extract hostname with locale from URL
  const getHostnameWithLocale = (url) => {
    if (!url) return window.location.hostname + window.location.pathname.split('/').slice(0, 2).join('/');
    try {
      const urlObj = new URL(url);
      return urlObj.hostname + urlObj.pathname.split('/').slice(0, 2).join('/');
    } catch {
      return window.location.hostname + window.location.pathname.split('/').slice(0, 2).join('/');
    }
  };
  
  // Push click event
  TagManager.dataLayer({
    dataLayer: {
      event: 'click',
      event_data: {
        component_name: componentName || '',
        type: type || 'button',
        link_text: linkText || '',
        link_url: linkUrl || window.location.href,
        link_hostname: linkHostname || getHostnameWithLocale(linkUrl),
        outbound: outbound !== undefined ? outbound : isOutbound(),
      }
    }
  });
};

// Track video events according to GTM specifications
export const trackVideoEvent = ({ eventType, currentTime, duration, videoTitle, videoUrl, visible = true }) => {
  // Clear previous event data
  TagManager.dataLayer({
    dataLayer: {
      event_data: null
    }
  });
  const videoPercent = duration > 0 ? Math.round((currentTime / duration) * 100) : 0;
  
  // Push video event
  TagManager.dataLayer({
    dataLayer: {
      event: eventType, // 'video_start', 'video_progress', or 'video_complete'
      event_data: {
        video_current_time: Math.round(currentTime),
        video_duration: Math.round(duration),
        video_percent: videoPercent,
        video_provider: 'self_hosted', // Since videos are hosted on CloudFront
        video_title: videoTitle || '',
        video_url: videoUrl || '',
        visible: visible,
      }
    }
  });
};